import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmResult, Log } from "@ryde/shared";

import { IModalOptions, useModalConfirm } from "../hooks-singletons";

type ApplyIfConfirmedResult = (func: () => void) => () => Promise<void>;

/** Boolean or lazy getter so close handlers can read fresh dirty state after awaits. */
type DirtyCheck = boolean | (() => boolean);

interface ICheckConfirmResultArguments {
  isDirty: DirtyCheck;
}

type IUseConfirmDirtyFieldArguments = Partial<IModalOptions>;

interface IUseConfirmDirtyFieldResult {
  applyIfConfirmed: (
    args: ICheckConfirmResultArguments,
  ) => ApplyIfConfirmedResult;
}

const resolveIsDirty = (isDirty: DirtyCheck): boolean =>
  typeof isDirty === "function" ? isDirty() : isDirty;

export const useConfirmDirtyField = ({
  title,
  customContent,
  cancelButtonTitle,
  okButtonTitle,
}: IUseConfirmDirtyFieldArguments): IUseConfirmDirtyFieldResult => {
  const { confirmDialogResult } = useModalConfirm();
  const { t } = useTranslation();
  // Blocks stacked Escape / backdrop clicks while a confirm dialog (or the
  // subsequent close) is already in flight for this form instance.
  const isCloseConfirmInFlightRef = useRef(false);

  const applyIfConfirmed = useCallback(
    ({ isDirty }: ICheckConfirmResultArguments): ApplyIfConfirmedResult =>
      (func: () => void) =>
      async (): Promise<void> => {
        if (isCloseConfirmInFlightRef.current) {
          Log.debug("Dialog applyIfConfirmed ignored: already in flight");
          return;
        }

        isCloseConfirmInFlightRef.current = true;
        try {
          // Re-resolve at call time so slow networks / concurrent saves cannot
          // use a stale dirty flag from an earlier render.
          if (!resolveIsDirty(isDirty)) {
            func();
            return;
          }

          Log.debug(`Dialog applyIfConfirmed, isDirty`);

          const confirmationResult = await confirmDialogResult({
            title: title ?? t(`general.CONFIRM`),
            customContent,
            cancelButtonTitle:
              cancelButtonTitle ?? t(`general.EXIT_WITHOUT_SAVING`),
            okButtonTitle: okButtonTitle ?? t(`general.BACK_TO_EDITING`),
          });

          if (
            [ConfirmResult.OK, ConfirmResult.CANCEL_BY_HEADER].includes(
              confirmationResult,
            )
          ) {
            return;
          }

          // User chose exit without saving — close exactly once.
          func();
        } finally {
          isCloseConfirmInFlightRef.current = false;
        }
      },
    [
      cancelButtonTitle,
      confirmDialogResult,
      customContent,
      okButtonTitle,
      t,
      title,
    ],
  );

  return { applyIfConfirmed };
};
